// Horas da equipe — /administrativo/horas (01/10/2026)
//
// Pedido do Lucas: "precisamos de um dashboard no administrativo para
// acompanhar as horas do pessoal". Uma tela só: quanto foi anotado contra o
// esperado, quem está em dia, quem tem hora faltando (e desde quando), e para
// onde as horas foram. Substitui o placar "Equipe" que morava em Operacional ›
// Horas — clicar no nome continua abrindo a semana da pessoa.
//
// Quem vê: `horas.ver_equipe` (conferido de novo no servidor). A régua — dia
// completo = 90% da carga anotada ou ausência; hoje ainda não conta — vem
// pronta do GET /financeiro/painel/horas; aqui só se filtra e se desenha.
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Alert, App as AntApp, Button, Card, Checkbox, Col, DatePicker, Empty, Input, Progress, Row, Segmented, Select,
  Space, Table, Tag, Typography,
} from 'antd';
import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { api } from '../operacional-horas/api';
import TotalHoras from '../operacional-horas/TotalHoras';
import MinhaSemana from '../operacional-horas/MinhaSemana';
import { formatarHoras } from '../operacional-horas/horasUtils';
import FaixaDias from './FaixaDias';
import {
  FILTRO_VAZIO, LEGENDA, LEGENDA_RATEIO_FIXO, LISTRAS_CLOCKIFY, PERIODOS, chaveDoDestino, corCobertura, corDoEstado, dataCurta, destinosDe, erroDoIntervalo,
  detalheSituacao, devendoDe, filtrarPessoas, fraseSituacao, opcoesDe, ordenarPorAtencao, pctBR, pctContrato, periodoDe, resumoDe, situacaoDe,
  type ChavePeriodo, type Filtro, type Painel, type PessoaPainel,
} from './painelUtils';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const COR_SITUACAO: Record<string, string> = {
  em_dia: '#389e0d', falta: '#d48806', atraso: '#cf1322', sem_esperado: '#8c8c8c', sem_ficha: '#8c8c8c',
  rateio_fixo: '#1d39c4',
};

function Cartao({ titulo, children, rodape }: { titulo: string; children: ReactNode; rodape?: ReactNode }) {
  return (
    <Card size="small" style={{ height: '100%' }} styles={{ body: { padding: '12px 16px' } }}>
      <Text type="secondary" style={{ fontSize: 12 }}>{titulo}</Text>
      <div style={{ fontSize: 26, lineHeight: 1.2, marginTop: 4 }}>{children}</div>
      {rodape && <div style={{ marginTop: 6, fontSize: 12, color: '#8c8c8c' }}>{rodape}</div>}
    </Card>
  );
}

export default function HorasDaEquipe() {
  const { message } = AntApp.useApp();
  const hojeLocal = dayjs().format('YYYY-MM-DD');
  const [chave, setChave] = useState<ChavePeriodo | 'livre'>('14d');
  const [intervalo, setIntervalo] = useState(() => periodoDe('14d', hojeLocal));
  const [painel, setPainel] = useState<Painel | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<Filtro>(FILTRO_VAZIO);
  const [pessoa, setPessoa] = useState<PessoaPainel | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setPainel(await api(`/financeiro/painel/horas?inicio=${intervalo.inicio}&fim=${intervalo.fim}`));
    } catch (e: any) {
      // Falha de leitura NÃO vira painel vazio: some o que estava na tela e fica o aviso.
      setPainel(null);
      setErro(e?.message || 'Não foi possível carregar o painel.');
    } finally { setCarregando(false); }
  }, [intervalo]);

  useEffect(() => { carregar(); }, [carregar]);

  const escolherAtalho = (v: ChavePeriodo) => { setChave(v); setIntervalo(periodoDe(v, hojeLocal)); };
  const escolherDatas = (ini: string, fim: string) => {
    const recusa = erroDoIntervalo(ini, fim);
    if (recusa) { message.warning(recusa); return; }
    setChave('livre');
    setIntervalo({ inicio: ini, fim });
  };

  const todas = useMemo(() => painel?.pessoas || [], [painel]);
  const visiveis = useMemo(() => ordenarPorAtencao(filtrarPessoas(todas, filtro)), [todas, filtro]);
  const resumo = useMemo(() => resumoDe(visiveis), [visiveis]);
  const destinos = useMemo(() => destinosDe(painel?.destinos || [], visiveis), [painel, visiveis]);
  const setores = useMemo(() => opcoesDe(todas, 'setor'), [todas]);
  const chefes = useMemo(() => opcoesDe(todas, 'chefe_nome'), [todas]);
  const filtrando = !!(filtro.busca.trim() || filtro.setor || filtro.chefe || filtro.soComFalta);
  const totalDestinos = destinos.reduce((s, d) => s + d.minutos, 0);
  // Horas do período = as anotadas + as presumidas de quem tem rateio fixo (que são trabalho também).
  const horasDoPeriodo = resumo.minutos + resumo.minutos_presumidos;
  const pctEmContrato = pctContrato(horasDoPeriodo, resumo.minutos_contrato);
  // Hora do Clockify em projeto que não está ligado a contrato: não é "interna" — só não se sabe de quem é.
  const semVinculo = destinos.find(d => chaveDoDestino(d) === 'ck:sem-vinculo')?.minutos || 0;

  // ── A semana de uma pessoa (clicou no nome) ───────────────────────────────
  if (pessoa) {
    return (
      <div>
        <Space style={{ marginBottom: 12 }} wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={() => { setPessoa(null); carregar(); }}>Voltar ao painel</Button>
          <Text strong>{pessoa.nome}</Text>
          <Text type="secondary">{pessoa.usuario_email}</Text>
        </Space>
        <Alert
          type="info" showIcon style={{ marginBottom: 12 }}
          message="Você está vendo as horas de outra pessoa"
          description="O que for alterado aqui muda o custo dos contratos dela no fechamento."
        />
        <Card styles={{ body: { paddingTop: 8 } }}><MinhaSemana emailAlvo={pessoa.usuario_email} /></Card>
      </div>
    );
  }

  return (
    <div>
      <Title level={4} style={{ marginTop: 0 }}>⏱️ Horas da equipe</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Quem anotou, quanto falta e para onde foram as horas. Está em dia quem anotou pelo menos 90% das horas
        esperadas no período — ausência registrada não conta como falta, e o dia de hoje ainda não entra na conta.
        No dia em que a pessoa não anotou nada aqui, vale o que ela lançou no Clockify. Quem tem rateio fixo no cadastro não precisa anotar.
      </Text>

      <Space wrap style={{ marginBottom: 12 }}>
        <Segmented
          options={PERIODOS} value={chave === 'livre' ? '' : chave}
          onChange={(v) => escolherAtalho(v as ChavePeriodo)}
        />
        <RangePicker
          allowClear={false} format="DD/MM/YYYY"
          value={[dayjs(intervalo.inicio), dayjs(intervalo.fim)]}
          onChange={(v) => { if (v && v[0] && v[1]) escolherDatas(v[0].format('YYYY-MM-DD'), v[1].format('YYYY-MM-DD')); }}
        />
        <Button icon={<ReloadOutlined />} onClick={carregar} loading={carregando}>Atualizar</Button>
      </Space>

      {erro && (
        <Alert
          type="error" showIcon style={{ marginBottom: 12 }}
          message="Não consegui carregar as horas da equipe"
          description={erro}
          action={<Button size="small" onClick={carregar}>Tentar de novo</Button>}
        />
      )}

      {painel && (
        <>
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={24} sm={12} lg={6}>
              <Cartao
                titulo={painel.fim >= painel.hoje ? 'Anotado até ontem' : 'Anotado'}
                rodape={resumo.carga_min > 0
                  ? <>
                      de {formatarHoras(resumo.carga_min)} esperadas{resumo.minutos_hoje > 0 ? ` · hoje: +${formatarHoras(resumo.minutos_hoje)}` : ''}
                      {resumo.minutos_clockify > 0 && ` · ${formatarHoras(resumo.minutos_clockify)} vieram do Clockify`}
                    </>
                  : 'sem horas esperadas neste período'}
              >
                <Space align="start" size={12}>
                  <TotalHoras minutos={resumo.minutos_cobertos} alinhar="start" tamanhoDecimal={12} />
                  {resumo.cobertura_pct !== null && (
                    <span style={{ color: corCobertura(resumo.cobertura_pct), fontWeight: 600 }}>{pctBR(resumo.cobertura_pct)}</span>
                  )}
                </Space>
              </Cartao>
            </Col>
            <Col xs={12} sm={12} lg={6}>
              <Cartao
                titulo="Pessoas em dia"
                rodape={<>
                  anotaram 90% ou mais do esperado
                  {resumo.pessoas_rateio_fixo > 0 && ` · ${resumo.pessoas_rateio_fixo} com rateio fixo ${resumo.pessoas_rateio_fixo === 1 ? 'não anota' : 'não anotam'}`}
                </>}
              >
                <span style={{ color: '#389e0d', fontWeight: 600 }}>{resumo.pessoas_em_dia}</span>
                <Text type="secondary" style={{ fontSize: 16 }}> de {resumo.pessoas_com_carga}</Text>
              </Cartao>
            </Col>
            <Col xs={12} sm={12} lg={6}>
              <Cartao
                titulo="Com hora faltando"
                rodape={resumo.pessoas_com_falta > 0
                  ? <>
                      faltam {formatarHoras(resumo.devendo_min)} no total
                      {resumo.pessoas_em_atraso > 0 && (
                        <span style={{ color: '#cf1322' }}> · {resumo.pessoas_em_atraso} com dia em aberto há 3 dias úteis ou mais</span>
                      )}
                    </>
                  : 'ninguém devendo hora'}
              >
                <span style={{ color: resumo.pessoas_com_falta > 0 ? '#d48806' : '#389e0d', fontWeight: 600 }}>
                  {resumo.pessoas_com_falta}
                </span>
                <Text type="secondary" style={{ fontSize: 16 }}> {resumo.pessoas_com_falta === 1 ? 'pessoa' : 'pessoas'}</Text>
              </Cartao>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Cartao
                titulo="Horas em contrato"
                rodape={horasDoPeriodo > 0
                  ? <>
                      {formatarHoras(resumo.minutos_contrato)} em contratos
                      {' · '}{formatarHoras(Math.max(0, horasDoPeriodo - resumo.minutos_contrato - semVinculo))} internas
                      {semVinculo > 0 && ` · ${formatarHoras(semVinculo)} do Clockify sem vínculo com contrato`}
                    </>
                  : 'nada anotado neste período'}
              >
                <span style={{ fontWeight: 600 }}>{pctEmContrato === null ? '—' : `${pctEmContrato}%`}</span>
              </Cartao>
            </Col>
          </Row>

          <Space wrap style={{ marginBottom: 8 }}>
            <Input.Search
              allowClear placeholder="Buscar pessoa" style={{ width: 200 }}
              value={filtro.busca} onChange={(e) => setFiltro(f => ({ ...f, busca: e.target.value }))}
            />
            <Select
              allowClear showSearch optionFilterProp="label" placeholder="Setor" style={{ width: 170 }}
              options={setores} value={filtro.setor} onChange={(v) => setFiltro(f => ({ ...f, setor: v || null }))}
            />
            <Select
              allowClear showSearch optionFilterProp="label" placeholder="Chefe direto" style={{ width: 190 }}
              options={chefes} value={filtro.chefe} onChange={(v) => setFiltro(f => ({ ...f, chefe: v || null }))}
            />
            <Checkbox
              checked={filtro.soComFalta}
              onChange={(e) => setFiltro(f => ({ ...f, soComFalta: e.target.checked }))}
            >
              Só quem tem hora faltando
            </Checkbox>
            {filtrando && <Button type="link" style={{ padding: 0 }} onClick={() => setFiltro(FILTRO_VAZIO)}>Limpar filtros</Button>}
            <Text type="secondary" style={{ fontSize: 12 }}>
              {visiveis.length === todas.length ? `${todas.length} pessoas` : `${visiveis.length} de ${todas.length} pessoas`}
            </Text>
          </Space>

          <Card styles={{ body: { padding: 0 } }} style={{ marginBottom: 12 }}>
            <Table<PessoaPainel>
              rowKey="usuario_email" size="small" dataSource={visiveis} loading={carregando}
              pagination={false} scroll={{ x: 'max-content' }}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Ninguém neste filtro" /> }}
              columns={[
                {
                  title: 'Pessoa', dataIndex: 'nome', fixed: 'left', width: 190,
                  sorter: (a, b) => a.nome.localeCompare(b.nome, 'pt-BR'),
                  render: (n: string, p) => (
                    <div>
                      <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => setPessoa(p)}>{n}</Button>
                      {p.sem_ficha && <Tag style={{ marginLeft: 6 }}>sem ficha</Tag>}
                      <div>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {[p.setor, p.chefe_nome ? `chefe: ${p.chefe_nome}` : null].filter(Boolean).join(' · ') || p.usuario_email}
                        </Text>
                      </div>
                    </div>
                  ),
                },
                {
                  title: 'Situação', key: 'situacao', width: 200,
                  sorter: (a, b) => devendoDe(a) - devendoDe(b),
                  render: (_: unknown, p) => {
                    const s = situacaoDe(p);
                    const detalhe = detalheSituacao(p);
                    return (
                      <div>
                        <span style={{ color: COR_SITUACAO[s] }}>{fraseSituacao(p)}</span>
                        {detalhe && <div><Text type="secondary" style={{ fontSize: 11 }}>{detalhe}</Text></div>}
                      </div>
                    );
                  },
                },
                {
                  title: 'Anotado', dataIndex: 'cobertura_pct', width: 130,
                  sorter: (a, b) => (a.cobertura_pct ?? -1) - (b.cobertura_pct ?? -1),
                  render: (pct: number | null) => (
                    <Progress
                      percent={Math.min(pct ?? 0, 100)} size="small" strokeColor={corCobertura(pct)}
                      format={() => pctBR(pct)}
                    />
                  ),
                },
                {
                  title: 'Horas', dataIndex: 'minutos_ate_ontem', width: 100, align: 'right',
                  sorter: (a, b) => a.minutos_ate_ontem - b.minutos_ate_ontem,
                  render: (m: number, p) => (
                    <span>
                      <TotalHoras minutos={m} alinhar="end" />
                      <div><Text type="secondary" style={{ fontSize: 11 }}>
                        {p.carga_min > 0
                          ? `de ${formatarHoras(p.carga_min)}`
                          : (p.minutos_presumidos || 0) > 0 ? `+ ${formatarHoras(p.minutos_presumidos || 0)} pelo rateio` : 'sem esperado'}
                      </Text></div>
                    </span>
                  ),
                },
                {
                  title: <span>Dia a dia <Text type="secondary" style={{ fontSize: 11, fontWeight: 400 }}>
                    {dataCurta(painel.inicio)} a {dataCurta(painel.fim)}</Text></span>,
                  key: 'dias',
                  render: (_: unknown, p) => <FaixaDias dias={p.dias} cargaDiaMin={Math.round(p.horas_dia * 60)} />,
                },
                {
                  title: 'Home office', dataIndex: 'dias_remoto', width: 110, align: 'center',
                  sorter: (a, b) => a.dias_remoto - b.dias_remoto,
                  render: (n: number) => (n ? `${n} dia${n > 1 ? 's' : ''}` : <Text type="secondary">—</Text>),
                },
                {
                  title: 'Campo', dataIndex: 'dias_campo', width: 75, align: 'center',
                  sorter: (a, b) => a.dias_campo - b.dias_campo,
                  render: (n: number) => (n ? `${n} dia${n > 1 ? 's' : ''}` : <Text type="secondary">—</Text>),
                },
              ]}
            />
            <div style={{ padding: '8px 12px', borderTop: '1px solid #f0f0f0', display: 'flex', flexWrap: 'wrap', gap: 14 }}>
              {LEGENDA.map(l => {
                const { fundo, borda } = corDoEstado(l.estado);
                return (
                  <span key={l.estado} style={{ fontSize: 11, color: '#8c8c8c', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ width: 12, height: 12, borderRadius: 2, background: fundo, border: `1px solid ${borda}`, display: 'inline-block' }} />
                    {l.rotulo}
                  </span>
                );
              })}
              {resumo.pessoas_rateio_fixo > 0 && (() => {
                const { fundo, borda } = corDoEstado(LEGENDA_RATEIO_FIXO.estado);
                return (
                  <span style={{ fontSize: 11, color: '#8c8c8c', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ width: 12, height: 12, borderRadius: 2, background: fundo, border: `1px solid ${borda}`, display: 'inline-block' }} />
                    {LEGENDA_RATEIO_FIXO.rotulo}
                  </span>
                );
              })()}
              {resumo.minutos_clockify > 0 && (
                <span style={{ fontSize: 11, color: '#8c8c8c', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <span style={{
                    width: 12, height: 12, borderRadius: 2, display: 'inline-block', backgroundColor: '#52c41a',
                    border: '1px solid #52c41a', backgroundImage: LISTRAS_CLOCKIFY,
                  }} />
                  listrado = anotado no Clockify
                </span>
              )}
              <span style={{ fontSize: 11, color: '#8c8c8c' }}>traço azul embaixo = home office · verde = campo</span>
            </div>
          </Card>

          <Card size="small" title="Para onde foram as horas" styles={{ body: { padding: 0 } }}>
            <Table
              rowKey={chaveDoDestino} size="small" dataSource={destinos}
              pagination={destinos.length > 15 ? { pageSize: 15, size: 'small', showSizeChanger: false } : false}
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nenhuma hora anotada neste período" /> }}
              columns={[
                {
                  title: 'Contrato ou centro de custo', dataIndex: 'rotulo',
                  render: (r: string, d) => (
                    <span>
                      <Text strong>{r}</Text>
                      {d.detalhe && <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>{d.detalhe}</Text>}
                    </span>
                  ),
                },
                { title: 'Pessoas', dataIndex: 'pessoas', width: 90, align: 'center' },
                {
                  title: 'Horas', dataIndex: 'minutos', width: 110, align: 'right',
                  render: (m: number) => <TotalHoras minutos={m} alinhar="end" />,
                },
                {
                  title: 'Do total', dataIndex: 'minutos', key: 'pct', width: 180,
                  render: (m: number) => {
                    const pct = totalDestinos > 0 ? Math.round((m / totalDestinos) * 100) : 0;
                    return <Progress percent={pct} size="small" format={() => `${pct}%`} />;
                  },
                },
              ]}
            />
          </Card>
        </>
      )}
    </div>
  );
}
